"use client";

import { useState } from "react";
import { InfoIcon, TrendingUp, TrendingDown } from "lucide-react";

interface CircularScoreProps {
  score: number;
  size?: "sm" | "md" | "lg";
  brandColor?: string;
  showVariability?: boolean;
  variabilityContext?: string;
}

function getCircularScoreColor(score: number, brandColor?: string): string {
  if (brandColor) return brandColor;
  if (score >= 70) return "#059669"; // green-600 (better for accessibility)
  if (score >= 45) return "#d97706"; // amber-600 (better contrast than yellow)
  return "#dc2626"; // red-600 (clear danger signal)
}

export function CircularScore({ 
  score, 
  size = "lg", 
  brandColor,
  showVariability = true,
  variabilityContext
}: CircularScoreProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const circumference = 2 * Math.PI * 40;
  const strokeDasharray = `${(score / 100) * circumference} ${circumference}`;
  const sizeClasses = size === "lg" ? "w-24 h-24" : size === "md" ? "w-20 h-20" : "w-16 h-16";
  const textSize = size === "lg" ? "text-xl" : size === "md" ? "text-lg" : "text-sm";
  const scoreColor = getCircularScoreColor(score, brandColor);
  
  // Variability range (scores can fluctuate ±3 points)
  const variability = 3;
  const minScore = Math.max(0, score - variability);
  const maxScore = Math.min(100, score + variability);
  
  return (
    <div className="relative">
      <div className={`relative ${sizeClasses}`}>
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="40"
            stroke="#e5e7eb"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="50"
            cy="50"
            r="40"
            stroke={scoreColor}
            strokeWidth="8"
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeLinecap="round"
          />
        </svg>
        <div className={`absolute inset-0 flex items-center justify-center ${textSize} font-bold`} style={{ color: scoreColor }}>
          {score}
        </div>
      </div>
      
      {/* Variability Indicator */}
      {showVariability && size === "lg" && (
        <div className="absolute -top-1 -right-1">
          <button
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            className="p-1 rounded-full bg-white shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors"
            aria-label="Score variability information"
          >
            <InfoIcon className="w-3 h-3 text-gray-400" />
          </button>
          
          {showTooltip && (
            <div className="absolute top-full right-0 mt-2 z-50 animate-in fade-in duration-100">
              <div className="px-3 py-2 rounded-lg border shadow-lg bg-white border-gray-200 w-56">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-700">Typical Range</span>
                  <div className="flex items-center gap-1">
                    <TrendingDown className="w-3 h-3 text-gray-400" />
                    <span className="text-xs font-semibold">{minScore}</span>
                    <span className="text-xs text-gray-400">—</span>
                    <span className="text-xs font-semibold">{maxScore}</span>
                    <TrendingUp className="w-3 h-3 text-gray-400" />
                  </div>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {variabilityContext || "Scores can vary ±3 points between measurements due to the inherent variability of AI responses. Track trends over time for more reliable insights."}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
